import { t } from '@web-solutions/module-localization';
import { PaymentSystem } from '@web-solutions/react-billing';

const TKEY = 'core.payment_failed_sheet';

const DEFAULT_REASON = 'generic';

/**
 * Stripe asks not to reveal these to the customer (they hint at fraud/theft and
 * telling the cardholder helps whoever is misusing the card). Show the generic
 * text instead — the real code still goes to analytics.
 * https://docs.stripe.com/declines/codes
 */
const SENSITIVE_CODES = [
  'fraudulent',
  'lost_card',
  'stolen_card',
  'merchant_blacklist',
  'pickup_card',
  'restricted_card',
  'security_violation',
  'stop_payment_order',
  'revocation_of_authorization',
  'revocation_of_all_authorizations',
];

/**
 * Stripe `code` / `decline_code` -> reason slug. The slug is both the
 * localization key (`core.payment_failed_sheet.reason.<slug>`) and the value
 * reported to analytics, so several codes that need the same wording collapse
 * into one slug.
 */
const REASON_BY_CODE: Record<string, string> = {
  // Money
  insufficient_funds: 'insufficient_funds',
  withdrawal_count_limit_exceeded: 'limit_exceeded',
  card_velocity_exceeded: 'limit_exceeded',
  invalid_amount: 'limit_exceeded',
  amount_too_large: 'limit_exceeded',
  amount_too_small: 'limit_exceeded',

  // Card data
  expired_card: 'expired_card',
  incorrect_cvc: 'incorrect_cvc',
  invalid_cvc: 'incorrect_cvc',
  incorrect_number: 'incorrect_number',
  invalid_number: 'incorrect_number',
  invalid_expiry_month: 'expired_card',
  invalid_expiry_year: 'expired_card',
  incorrect_zip: 'incorrect_zip',
  incorrect_address: 'incorrect_zip',

  // Issuer said no
  do_not_honor: 'contact_issuer',
  call_issuer: 'contact_issuer',
  approve_with_id: 'contact_issuer',
  invalid_account: 'contact_issuer',
  new_account_information_available: 'contact_issuer',
  reenter_transaction: 'try_later',

  // Card can't be used for this purchase
  transaction_not_allowed: 'not_allowed',
  service_not_allowed: 'not_allowed',
  card_not_supported: 'not_supported',
  currency_not_supported: 'not_supported',

  // Transient
  processing_error: 'try_later',
  try_again_later: 'try_later',
  issuer_not_available: 'try_later',
  duplicate_transaction: 'duplicate',

  // SCA
  authentication_required: 'authentication_required',
  authentication_not_handled: 'authentication_required',
  payment_intent_authentication_failure: 'authentication_required',

  // Explicitly generic
  card_declined: DEFAULT_REASON,
  generic_decline: DEFAULT_REASON,
  testmode_decline: DEFAULT_REASON,
};

const isSolidgateCode = (code: string) => /^\d+\.\d+$/.test(code);

const firstString = (...candidates: any[]): string | undefined => {
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate) {
      return candidate;
    }
  }
  return undefined;
};

/**
 * Digs the provider code out of whatever the payment layer threw. The billing
 * interceptor puts the backend payload on `error.data` and copies `data.code`
 * onto `error.code` (packages/module-billing/api/network.js), but Stripe's real
 * reason usually sits one level deeper in `decline_code`, which is more
 * specific than the `card_declined` umbrella code — so it wins when present.
 */
export const extractPaymentErrorCode = (error: any): string | undefined => {
  if (!error) {
    return undefined;
  }

  if (typeof error === 'string') {
    return error;
  }

  const data = error.data || {};
  const nested = data.error || error.error || {};
  const lastPaymentError = error.last_payment_error || data.last_payment_error || {};

  return firstString(
    error.decline_code,
    data.decline_code,
    nested.decline_code,
    lastPaymentError.decline_code,
    error.code,
    data.code,
    nested.code,
    lastPaymentError.code,
    data.status,
  );
};

/** Keys that only lead into transport noise (axios internals, serialized causes). */
const DECLINE_SEARCH_SKIP_KEYS = ['config', 'request', 'headers', 'cause', 'stack'];

const MAX_DECLINE_SEARCH_DEPTH = 5;

const DECLINE_CODE_KEYS = ['decline_code', 'declineCode'];

/** Solidgate reports its code as a number in some shapes, Stripe always as a string. */
const RESPONSE_CODE_KEYS = ['code', 'error_code', 'errorCode'];

const firstScalar = (...candidates: any[]): string | undefined => {
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate) {
      return candidate;
    }
    if (typeof candidate === 'number' && Number.isFinite(candidate)) {
      return String(candidate);
    }
  }
  return undefined;
};

/**
 * A code shows up at a different depth per flow: top level on a Stripe.js
 * error, under `data` / `data.error` behind the billing interceptor, under
 * `last_payment_error` or `payment_intent.last_payment_error` on a 200 with an
 * unfinished intent. Instead of listing every shape (and silently reporting
 * nothing for the one that was missed), walk the error and take the first match.
 */
const findErrorField = (error: any, keys: string[]): string | undefined => {
  const seen = new Set<any>();
  // Level by level, so the shallowest (= most relevant) value wins.
  let level: any[] = [error];

  for (let depth = 0; depth <= MAX_DECLINE_SEARCH_DEPTH && level.length; depth += 1) {
    const nextLevel: any[] = [];

    for (const value of level) {
      if (!value || typeof value !== 'object' || seen.has(value)) {
        continue;
      }

      seen.add(value);

      const own = firstScalar(...keys.map((key) => value[key]));

      if (own) {
        return own;
      }

      for (const [key, nested] of Object.entries(value)) {
        if (!DECLINE_SEARCH_SKIP_KEYS.includes(key)) {
          nextLevel.push(nested);
        }
      }
    }

    level = nextLevel;
  }

  return undefined;
};

/**
 * Digs out ONLY the decline code — the provider's reason for refusing the
 * charge (`insufficient_funds`, `do_not_honor`, ...), as opposed to the umbrella
 * `code` (`card_declined`). Undefined when the response carries no decline
 * reason (network failures, validation errors).
 *
 * Stripe only: other providers don't send a separate decline reason (Solidgate
 * puts everything in its numeric `code`), so the caller must pass the payment
 * system and the field stays absent everywhere else.
 *
 * Reported as its own analytics field so a declined charge can be broken down
 * by bank reason instead of collapsing into `card_declined`.
 */
export const extractDeclineCode = (
  error: any,
  paymentSystem?: PaymentSystem | string,
): string | undefined => {
  if (paymentSystem !== PaymentSystem.STRIPE) {
    return undefined;
  }

  return findErrorField(error, DECLINE_CODE_KEYS);
};

/**
 * The payment system's own response code — Stripe's umbrella `card_declined`,
 * Solidgate's numeric code, an axios `ERR_BAD_RESPONSE` on a transport failure.
 *
 * Searched at every depth for the same reason the decline reason is. Reading
 * only `error.code` reported nothing for every shape where the code sits deeper
 * — which is how a declined charge reached the analytics with `decline_code`
 * filled and `code` empty, the two being extracted asymmetrically.
 *
 * Unlike the decline reason this is not gated on the payment system: every
 * provider sends a code.
 */
export const extractResponseCode = (error: any): string | undefined =>
  findErrorField(error, RESPONSE_CODE_KEYS);

/** Reason slug for a provider code — safe for analytics and text lookup. */
export const getPaymentErrorReason = (code?: string): string => {
  if (!code) {
    return DEFAULT_REASON;
  }

  const normalized = code.toString().trim().toLowerCase();

  if (SENSITIVE_CODES.includes(normalized)) {
    return DEFAULT_REASON;
  }

  return REASON_BY_CODE[normalized] || DEFAULT_REASON;
};

export type PaymentErrorContent = {
  /** Raw provider code, for analytics. Undefined when the provider sent none. */
  code?: string;
  /** Normalized reason slug. Always set — falls back to `generic`. */
  reason: string;
  /** Human-readable explanation for the user. */
  text: string;
};

/**
 * Turns a payment error into the copy shown in the payment-failed sheet.
 * Unknown, missing and sensitive codes all resolve to the generic text, so the
 * sheet is always showable.
 */
export const getPaymentErrorContent = (error: any, tKey: string = TKEY): PaymentErrorContent => {
  const code = extractPaymentErrorCode(error);
  const reason = getPaymentErrorReason(code);

  // Solidgate keeps its own detailed copy per numeric code — reuse it instead of
  // degrading those funnels to the generic line.
  if (code && isSolidgateCode(code)) {
    const solidgateText = t(`core.solidgate_payment_errors.${code}`, { defaultValue: '' });

    if (solidgateText) {
      return { code, reason: code, text: solidgateText };
    }
  }

  return {
    code,
    reason,
    text: t(`${tKey}.reason.${reason}`, {
      defaultValue: t(`${tKey}.reason.${DEFAULT_REASON}`),
    }),
  };
};
